import React, { useState, useEffect } from "react";
import "./MobileChatMessages.scss";

const MobileChatMessages = ({
  properties,
  mobileMessageText,
  setMobileMessageText,
  mobileRecipients,
  setMobileRecipients,
  handleMobileBroadcast,
  isLoading,
  isError,
  errorMessage,
  profile,
}) => {
  const [messageType, setMessageType] = useState("custom");
  const [messageStatus, setMessageStatus] = useState(null);
  const [selectedRoleTakers, setSelectedRoleTakers] = useState([]);

  // Templates for common messages
  const messageTemplates = [
    {
      type: "rent_reminder",
      text: "Dear support team, I'd like to inform you that I've completed my payment for this month.",
    },
    {
      type: "maintenance",
      text: "Hello, I'd like to report a maintenance issue. [Describe your issue here].",
    },
    {
      type: "security",
      text: "I'd like to report a security concern. [Describe your concern here].",
    },
    {
      type: "inquiry",
      text: "Hello, I have a question about [your question here].",
    },
    {
      type: "feedback",
      text: "I'd like to provide some feedback about [your subject here].",
    },
  ];

  // Get role takers from profile
  const roleTakers = profile?.data?.role_takers || [];
  
  // Add caretaker if available
  const allContacts = [...roleTakers];
  if (profile?.data?.tenant_details?.caretaker?.name) {
    allContacts.push({
      _id: `caretaker-${Date.now()}`,
      name: profile.data.tenant_details.caretaker.name,
      phone: profile.data.tenant_details.caretaker.phone_number,
      role: "Caretaker",
    });
  }

  // Handle role taker selection
  const handleRoleTakerSelection = (roleTakerId) => {
    if (selectedRoleTakers.includes(roleTakerId)) {
      setSelectedRoleTakers(selectedRoleTakers.filter(id => id !== roleTakerId));
    } else {
      setSelectedRoleTakers([...selectedRoleTakers, roleTakerId]);
    }
    
    // Update the mobile recipients
    const updatedRecipients = selectedRoleTakers.includes(roleTakerId)
      ? selectedRoleTakers.filter(id => id !== roleTakerId)
      : [...selectedRoleTakers, roleTakerId];
      
    setMobileRecipients(updatedRecipients);
  };

  // Select all role takers
  const handleSelectAllContacts = () => {
    const allIds = allContacts.map(contact => contact._id);
    setSelectedRoleTakers(allIds);
    setMobileRecipients(allIds);
  };

  // Handle template selection
  const handleTemplateChange = (e) => {
    const type = e.target.value;
    setMessageType(type);

    // Find the template text
    if (type !== "custom") {
      const template = messageTemplates.find((t) => t.type === type);
      if (template) {
        setMobileMessageText(template.text);
      }
    } else {
      setMobileMessageText("");
    }
  };

  // Reset message status after 5 seconds
  useEffect(() => {
    if (messageStatus) {
      const timer = setTimeout(() => {
        setMessageStatus(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [messageStatus]);

  // Handle message submission
  const sendMessage = () => {
    handleMobileBroadcast();
    setMessageStatus("Message sent successfully!");
  };

  return (
    <div className="mobile-messages-container">
      <div className="message-wrapper">
        <h2 className="page-title">Message Support Team</h2>

        {isError && (
          <div className="alert error">
            {errorMessage}
          </div>
        )}

        {messageStatus && (
          <div className="alert success">
            {messageStatus}
          </div>
        )}

        <div className="info-panel">
          <p>
            Send a message to multiple members of your support team at once.
            Choose from a template or create your own message.
          </p>
        </div>

        <div className="form-grid">
          <div className="form-row">
            <div className="recipients-section">
              <div className="section-header">
                <h3>Select Recipients</h3>
                <button 
                  className="btn-outline-small"
                  onClick={handleSelectAllContacts}
                >
                  Select All
                </button>
              </div>

              <div className="tenants-list">
                {allContacts.length > 0 ? (
                  allContacts.map((contact) => (
                    <div className="tenant-checkbox" key={contact._id}>
                      <input
                        type="checkbox"
                        id={`contact-${contact._id}`}
                        checked={selectedRoleTakers.includes(contact._id)}
                        onChange={() => handleRoleTakerSelection(contact._id)}
                      />
                      <label htmlFor={`contact-${contact._id}`}>
                        {`${contact.name || "Unnamed Contact"} - ${contact.role || "Staff"}`}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="empty-list-message">
                    No contacts available
                  </p>
                )}
              </div>

              <div className="selected-recipients">
                <h4>Selected: {selectedRoleTakers.length} recipient(s)</h4>
                <div className="selected-chips">
                  {allContacts
                    .filter(contact => selectedRoleTakers.includes(contact._id))
                    .map((contact) => (
                      <span className="chip" key={contact._id}>
                        <span className="chip-label">{contact.name}</span>
                        <button 
                          className="chip-delete" 
                          onClick={() => handleRoleTakerSelection(contact._id)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="message-type">Message Template</label>
              <select
                id="message-type"
                value={messageType}
                onChange={handleTemplateChange}
              >
                <option value="custom">Custom Message</option>
                <option value="rent_reminder">Payment Notification</option>
                <option value="maintenance">Maintenance Request</option>
                <option value="security">Security Concern</option>
                <option value="inquiry">General Inquiry</option>
                <option value="feedback">Feedback</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message-content">Message Content</label>
              <textarea
                id="message-content"
                rows="4"
                value={mobileMessageText}
                onChange={(e) => setMobileMessageText(e.target.value)}
                disabled={messageType !== "custom" && messageType !== ""}
                placeholder="Type your message here..."
              ></textarea>
            </div>

            <div className="message-info">
              <span className="message-count">
                Character count: {mobileMessageText.length}
              </span>

              <span className="message-total">
                Total messages: {selectedRoleTakers.length}
              </span>
            </div>
          </div>

          <div className="form-row button-row">
            <button
              className={`btn-primary ${(
                selectedRoleTakers.length === 0 ||
                !mobileMessageText.trim() ||
                isLoading
              ) ? 'disabled' : ''}`}
              onClick={sendMessage}
              disabled={
                selectedRoleTakers.length === 0 ||
                !mobileMessageText.trim() ||
                isLoading
              }
            >
              {isLoading ? (
                <div className="spinner"></div>
              ) : (
                "Send Message"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileChatMessages;
